import { z } from 'zod';

/**
 * Business or label name: letters in any script, marks, digits, and trading-name
 * punctuation (`&`, `#`, parentheses, comma, period, hyphen, apostrophes).
 *
 * Letters match by Unicode property, not `a-z`, because this class also guards
 * `primaryContact.fullName`, where an accented value (`Juan Andrés Castellanos`)
 * would otherwise fail the whole provider document on read.
 */
const BUSINESS_NAME_CHAR_CLASS = /^[\p{L}\p{M}0-9\s\-&.,'‘’()#]+$/u;
// Business name validation schema
const legalNameSchema = z
    .string()
    .min(1, 'Business name is required')
    .min(2, 'Business name must be at least 2 characters')
    .max(100, 'Business name must be less than 100 characters')
    .regex(BUSINESS_NAME_CHAR_CLASS, 'Business name contains invalid characters');
// Name validation schema
const nameSchema = z
    .string()
    .min(1, 'Name is required')
    .min(2, 'Name must be at least 2 characters')
    .max(50, 'Name must be less than 50 characters')
    .regex(BUSINESS_NAME_CHAR_CLASS, 'Name contains invalid characters');
/**
 * Regex character class for valid person name characters.
 * Includes: Unicode letters (\p{L}), combining marks (\p{M}), whitespace,
 * hyphens, periods, and apostrophes (ASCII U+0027, left U+2018, right U+2019).
 * Uses unicode escapes for curly apostrophes to prevent encoding issues.
 */
const PERSON_NAME_CHAR_CLASS = /^[\p{L}\p{M}\s'\u2018\u2019.-]+$/u;
/**
 * At least one letter. `PERSON_NAME_CHAR_CLASS` alone admits pure punctuation
 * (`--`, `..`, `'`) and whitespace-only values.
 */
const PERSON_NAME_REQUIRES_LETTER = /\p{L}/u;
/**
 * A person's name. Unlike `nameSchema` (business/label text), this rejects
 * digits, `&`, `#` and parentheses, accepts letters in any script, and allows a
 * single character: José, Núñez, Zoë, O'Brien, Smith-Jones, St. James all pass.
 *
 * Trims before measuring and storing, and requires a letter, so whitespace-only
 * or punctuation-only input never saves as somebody's name.
 */
const personNameSchema = z
    .string()
    .trim()
    .min(1, 'Name is required')
    .max(100, 'Max 100 characters')
    .regex(PERSON_NAME_CHAR_CLASS, 'Invalid characters')
    .regex(PERSON_NAME_REQUIRES_LETTER, 'Must include a letter');
/**
 * A person's last name with stricter length requirements.
 *
 * Uses the same character set as `personNameSchema` but requires at least 2
 * characters. Lender APIs reject single-character last names, causing
 * credit applications to fail. First names can remain single-character (e.g. "T")
 * but last names must be at least 2 characters to pass lender validation.
 *
 * The schema trims whitespace before checking length to prevent " C " from
 * bypassing the min(2) constraint.
 */
const personLastNameSchema = z
    .string()
    .transform((val) => val.trim())
    .pipe(z
    .string()
    .min(2, 'Use at least 2 characters')
    .max(100, 'Max 100 characters')
    .regex(PERSON_NAME_CHAR_CLASS, 'Invalid characters')
    .regex(PERSON_NAME_REQUIRES_LETTER, 'Must include a letter'));

export { legalNameSchema, nameSchema, personLastNameSchema, personNameSchema };
//# sourceMappingURL=name.js.map
